import React, { useMemo, useState } from 'react'

import { AIMS } from '@disco/constants/FeatureTypes'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { resetSelection } from '@disco/state-management/actions/SelectionActions'
import {
  insertAggregatedTrack,
  insertItem,
} from '@disco/state-management/actions/ShortlistActions'
import { isFeatureEnabled } from '@disco/state-management/selectors/business'
import { getResultsSourceLabel } from '@disco/utils/search-page'
import {
  isPersistedAudioTrack,
  mustImportBeforeAdding,
} from '@disco/utils/tracks'

import type { Shortlist } from 'types/shortlist'
import type { Track, TrackId } from 'types/track'

import useLocalStorage from 'hooks/use-local-storage'
import { useDispatch, useSelector } from 'hooks/use-redux'

import {
  ADD_SUGGESTED_TRACK_EVENT,
  getAddSuggestionProps,
  getSuggestionDragData,
} from './analytics'
import ShortlistSuggestions from './index'
import { useTrackSuggestions } from './use-track-suggestions'

type Props = { shortlist: Shortlist }

/** Suggests tracks similar to a playlist's, to add with + or by dragging. */
const ShortlistSuggestionsContainer = ({ shortlist }: Props) => {
  const dispatch = useDispatch()
  const [collapsed, setCollapsed] = useLocalStorage(
    'suggested_tracks_collapsed',
    false,
  )
  const aimsEnabled = useSelector(state =>
    isFeatureEnabled(state, { featureName: AIMS }),
  )
  const [addingId, setAddingId] = useState<TrackId | null>(null)
  const [addError, setAddError] = useState(false)
  const playlistItems = useMemo(
    () => shortlist.items.filter(({ track }) => isPersistedAudioTrack(track)),
    [shortlist.items],
  )
  const playlistTracks = useMemo(
    () => playlistItems.map(item => item.track),
    [playlistItems],
  )
  const lastAddedTrack =
    playlistTracks.find(track => track.id === shortlist.lastAddedTrackId) ??
    playlistTracks[playlistTracks.length - 1]
  const existingIds = useMemo(
    () =>
      new Set(
        playlistItems.flatMap(({ track, importedFromTrackId }) =>
          importedFromTrackId === undefined
            ? [track.id]
            : [track.id, importedFromTrackId],
        ),
      ),
    [playlistItems],
  )
  const {
    tracks,
    loading,
    error,
    retry,
    refresh,
    canRefresh,
    referenceName,
    seedReady,
    searchId,
  } = useTrackSuggestions(lastAddedTrack, playlistTracks, aimsEnabled, {
    enabled: !collapsed,
    excludedTrackIds: existingIds,
  })

  const analyticsSearch = {
    searchId,
    resultsSource: getResultsSourceLabel(aimsEnabled, false),
  }

  const addTrack = async (track: Track, index: number) => {
    setAddingId(track.id)
    setAddError(false)
    try {
      // A failed import resolves to false and is already reported by
      // addAggregatedTrackToDisco, so only unexpected errors are shown here.
      if (mustImportBeforeAdding(track)) {
        const imported = await dispatch(
          insertAggregatedTrack(shortlist.id, track),
        )
        if (!imported) return
      } else {
        await dispatch(insertItem(shortlist.id, { track }))
      }
      dispatch(
        sendMixpanelEvent(
          ADD_SUGGESTED_TRACK_EVENT,
          getAddSuggestionProps(track, index + 1, analyticsSearch),
        ),
      )
    } catch {
      setAddError(true)
    } finally {
      setAddingId(null)
    }
  }

  return (
    <ShortlistSuggestions
      shortlistId={shortlist.id}
      tracks={tracks}
      loading={loading}
      error={error}
      canRefresh={canRefresh}
      seedReady={seedReady}
      referenceName={referenceName}
      collapsed={collapsed}
      addingId={addingId}
      addError={addError}
      getDragData={track => getSuggestionDragData(track, analyticsSearch)}
      onToggleCollapsed={() => {
        setAddError(false)
        setCollapsed(!collapsed)
      }}
      onRefresh={() => {
        setAddError(false)
        refresh()
      }}
      onRetry={() => {
        setAddError(false)
        retry()
      }}
      onAdd={addTrack}
      onDragStart={() => dispatch(resetSelection())}
    />
  )
}

export default ShortlistSuggestionsContainer
